import React, { FC, memo } from "react";
import { useLocation } from "react-router-dom";
import { useMedia } from "react-use";
import { useRouter } from "router";

import { IFetcherRenderProps } from "@/components/Fetcher";
import { FullScreenRow } from "@/components/Grid";
import { errorEntryType, hasCode } from "@/core/api";
import { MEDIA_SIZE_SM } from "@/core/media";
import { fetchPublicJobRequest, IJobRequest } from "@/data/job-requests";
import { ISessionUser } from "@/data/session";
import { useFetcher } from "@/hooks/http";
import { IWithRouteMatchProps } from "@/infrastructure/Route";
import { IWithSessionProps, withSession } from "@/infrastructure/Session";
import { Container } from "@/views/components/Container";
import { JobRequestHeader } from "@/views/components/JobRequests/Partials/JobRequestIconHeader/JobRequestHeader";
import { PublicLayout } from "@/views/components/Layout";
import { withHttpError } from "@/views/Errors";
import { router } from "@/views/router";

import { PublicJobRequestDetail } from "../JobRequests";
import { transformRouteProps } from "../JobRequests/selectors";

import "./_landing-job-request-detail.scss";

interface Props extends IWithSessionProps<ISessionUser>, IWithRouteMatchProps, IFetcherRenderProps<IJobRequest> {}

export const LandingJobRequestDetailComponent: FC<Props> = memo(props => {
  const {
    session: { authenticated },
    location: { search },
  } = props;
  const location = useLocation();
  const { jobRequestId } = transformRouteProps(props);
  const isSmallScreen = useMedia(MEDIA_SIZE_SM);
  const fetcherState = useFetcher(fetchPublicJobRequest, { jobRequestId }, [jobRequestId]);
  const { push } = useRouter();

  if (authenticated) {
    push(`${router.get("job-requests.detail", { jobRequestId }).path}${search}`);
    return null;
  } else if (fetcherState.error && hasCode(errorEntryType(fetcherState.error.payload), "NOT_FOUND")) {
    push(router.get("login").path, { redirect: location.pathname + location.search });
  }

  const baseClass = "landing-job-request-detail";

  return (
    <div className={baseClass}>
      <PublicLayout hideTopbar={false} hideFooter={false} borderBottomTopbar={false} stickyTopbar={isSmallScreen}>
        <JobRequestHeader {...fetcherState} />

        <Container padding={isSmallScreen ? ["md", false] : ["md", "md", "xxl", "md"]}>
          <FullScreenRow>
            <PublicJobRequestDetail {...fetcherState} />
          </FullScreenRow>
        </Container>
      </PublicLayout>
    </div>
  );
});

export const LandingJobRequestDetail = withSession(withHttpError(LandingJobRequestDetailComponent));
